import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Globe, KeyRound, Copy, ChevronDown, RefreshCw, Plus } from "lucide-react";
import { webIntentApi, apiErrorMessage } from "@/api/client";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";

const STATUS_META = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  not_seen: "bg-slate-100 text-slate-500 border-slate-200",
  "stale_>7d": "bg-amber-50 text-amber-700 border-amber-200",
};

const TAG_SNIPPET = (domain) => `<script async
  src="https://cdn.your-site.com/tag/leadctrl-tag.v1.js"
  data-site-id="${domain}"
  data-endpoint="https://${domain}/lc-collect"
  data-consent-cookie="cookie_consent"></script>`;

export default function WebsiteIntentAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [newSecret, setNewSecret] = useState(null);

  const load = () => webIntentApi.status().then((r) => setItems(r.data.items)).catch((e) => toast.error(apiErrorMessage(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  const rotate = async (id) => {
    try { const { data } = await webIntentApi.rotateSecret(id); setNewSecret({ id, secret: data.hmac_secret }); toast.success("Secret rotated — copy it now"); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const addSite = async () => {
    const domain = window.prompt("Site domain (e.g. weddings.example.com)");
    if (!domain) return;
    const product = window.prompt("Product code (CATERCTRL / VOWGENDA / CENAMOS / HOSTRELAY)", "CATERCTRL");
    if (!product) return;
    try { const { data } = await webIntentApi.createSite({ site_domain: domain, product_code: product }); setNewSecret({ id: data.id, secret: data.hmac_secret }); toast.success("Site created — copy the secret now"); load(); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 py-5 md:py-8" data-testid="website-intent-admin">
      <button onClick={() => navigate("/more")} className="mb-3 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800">
        <ArrowLeft className="h-4 w-4" /> More
      </button>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-extrabold tracking-tight text-slate-900 md:text-2xl">Website Intent</h1>
          <p className="mt-0.5 text-xs text-slate-400">Per-product-site ingestion health. Secrets are shown once at creation or rotation.</p>
        </div>
        <button data-testid="wi-add-site" onClick={addSite} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-teal-700 px-3 text-sm font-semibold text-white hover:bg-teal-800">
          <Plus className="h-4 w-4" /> Add site
        </button>
      </div>

      {newSecret && (
        <div data-testid="wi-new-secret" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-bold text-amber-800">Store this secret now — it will not be shown again.</p>
          <div className="mt-1 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded bg-white px-2 py-1 text-[11px] text-slate-700">{newSecret.secret}</code>
            <button onClick={() => copy(newSecret.secret)} className="rounded-md border border-amber-300 p-1.5 text-amber-700"><Copy className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}

      {loading ? <div className="h-32 animate-pulse rounded-xl border border-slate-200 bg-white" /> :
        items.length === 0 ? <EmptyState testid="wi-empty" icon={Globe} title="No product sites yet" description="Add a site to generate its ingestion secret and install snippet." /> :
        <div className="space-y-2.5" data-testid="wi-list">
          {items.map((s) => (
            <div key={s.id} data-testid={`wi-site-${s.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-teal-600" />
                    <span className="truncate font-semibold text-slate-800">{s.site_domain}</span>
                    <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{s.product_code}</span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                    <span className={`rounded-full border px-2 py-0.5 font-bold ${STATUS_META[s.installation_status] || STATUS_META.not_seen}`} data-testid={`wi-status-${s.id}`}>{s.installation_status}</span>
                    <span>Events 7d: <b className="text-slate-700">{s.event_count_7d}</b></span>
                    <span>Signature: <b className={s.signature_status === "ok" ? "text-emerald-600" : "text-rose-600"}>{s.signature_status}</b></span>
                    {s.last_matched_organisation && <span>Last match: {s.last_matched_organisation}</span>}
                  </div>
                </div>
                <button data-testid={`wi-rotate-${s.id}`} onClick={() => rotate(s.id)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-50">
                  <KeyRound className="h-3 w-3" /> Rotate
                </button>
              </div>
              <button data-testid={`wi-install-toggle-${s.id}`} onClick={() => setOpen(open === s.id ? null : s.id)} className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700">
                <ChevronDown className={`h-3 w-3 transition-transform ${open === s.id ? "rotate-180" : ""}`} /> Install instructions
              </button>
              {open === s.id && (
                <div className="mt-2 space-y-2" data-testid={`wi-install-${s.id}`}>
                  <p className="text-[11px] text-slate-500">Endpoint: <code className="rounded bg-slate-100 px-1">{s.ingestion_endpoint}</code>. Sign requests via a same-origin proxy (see docs/web_tag_deployment.md).</p>
                  <div className="relative">
                    <pre className="overflow-x-auto rounded-lg bg-slate-900 p-3 text-[10px] leading-relaxed text-slate-100">{TAG_SNIPPET(s.site_domain)}</pre>
                    <button onClick={() => copy(TAG_SNIPPET(s.site_domain))} className="absolute right-2 top-2 rounded-md bg-slate-700 p-1 text-slate-200"><Copy className="h-3 w-3" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>}
    </div>
  );
}
